import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { Project } from '../firestore-models/project';
import { Notice } from '../firestore-models/notice';
import { ApiNotice, ApiSetNoticesRequest } from '../api-client-module';

import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { NoticeManagementDialogComponent } from './notice-management-dialog/notice-management-dialog.component';
import { TranslateService } from '@ngx-translate/core';


import * as moment from 'moment-timezone';

@Component({
  selector: 'app-notice-management',
  templateUrl: './notice-management.component.html',
  styleUrls: ['./notice-management.component.css'],
  standalone: false,
})
export class NoticeManagementComponent implements OnInit {

  // コレクション
  private noticesCollection!: AngularFirestoreCollection<Notice>;
  noticesObservable!: Observable<Notice[]>;
  noticesSubscribe!: Subscription;
  private projectsCollection!: AngularFirestoreCollection<Project>;
  projectsObservable!: Observable<Project[]>;
  projectsSubscribe!: Subscription;

  // 通知情報
  displayedColumns: string[] = ['position', 'edit', 'select', 'projects', 'type', 'date', 'title'];
  dataSource = new MatTableDataSource<Notice>();
  selection = new SelectionModel<Notice>(true, []);

  protected TYPE_SEARCH_WORD_PREFIX: string = 'notice_type';
  protected CONFIRMED_SEARCH_WORD_PREFIX: string = 'confirmed';
  protected PROJECT_SEARCH_WORD_PREFIX: string = 'project_id';
  projects!: Array<Project>;

  searchFilter: any = {};

  noticeTypes: any = [
    { type: '', name: '' },
    { type: 'info', name: this.translate.instant('情報') },
    { type: 'warn', name: this.translate.instant('Warning') },
    { type: 'err', name: this.translate.instant('notice-management.ERROR') },
  ]

  private newArrivalDate!: Date;

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService

  ) {
    moment.tz.setDefault('Asia/Tokyo');
  }

  ngOnInit(): void {
    this.newArrivalDate = moment().add(-7, 'days').toDate();
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setCustomFilter();
    // 通知取得
    this.noticesCollection = this.angularFirestore.collection<Notice>(
      AppConst.FIRESTORE_NOTICES, ref => ref.orderBy('created', 'desc'));
    this.noticesObservable = this.noticesCollection.valueChanges({ idField: 'id' });
    this.noticesSubscribe = this.noticesObservable.subscribe(notices => {
      this.dataSource.data = this.addFilterValue(notices);
    });
    // プロジェクト取得
    this.projectsCollection = this.angularFirestore.collection<Project>(
      AppConst.FIRESTORE_PROJECTS, ref => ref.orderBy('project_id', 'asc'));
    this.projectsObservable = this.projectsCollection.valueChanges({ idField: 'id' });
    this.projectsSubscribe = this.projectsObservable.subscribe(projects => {
      this.projects = projects;
    });
  }

  ngOnDestroy(): void {
    this.noticesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Notice): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(notice: Notice): void {
    let apiNotice: ApiNotice = {
      id: notice.id,
      project_ids: notice.project_ids,
      notice_title: notice.notice_title,
      notice_detail: notice.notice_detail,
      notice_type: notice.notice_type,
      confirmed: notice.confirmed,
    };
    const dialogRef = this.dialog.open(NoticeManagementDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, notice: apiNotice }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(NoticeManagementDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, notice: null }
    });
  }

  //削除
  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedNotices: Array<ApiNotice> = new Array<ApiNotice>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedNotice: ApiNotice = {
            id: row.id,
            project_ids: row.project_ids,
            notice_title: row.notice_title,
            notice_detail: row.notice_detail,
            notice_type: row.notice_type,
            confirmed: row.confirmed,
          };
          selectedNotices.push(selectedNotice);
        }
      }
      if (!!result) {
        let request: ApiSetNoticesRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          notices: selectedNotices
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setNotices(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  isNew(notice: Notice): boolean {
    let ret = false;
    if (this.newArrivalDate < notice.created.toDate()) {
      ret = true;
    }
    return ret;
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'type':
          return compare(a.notice_type, b.notice_type, isAsc);
        case 'date':
          return compare(a.created.toMillis(), b.created.toMillis(), isAsc);
        default:
          return 0;
      }
    });
  }

  setCustomFilter() {
    // NOTE: AND検索
    this.dataSource.filterPredicate = (data: any, filter: any) => {
      let matchCount = 0;
      let keys = Object.keys(filter);
      for (const key of keys) {
        if (!filter[key]) {
          // フィルタに値がない場合は全抽出
          matchCount++;
        } else {
          if (data[key].trim().toLowerCase().indexOf(filter[key]) !== -1) {
            matchCount++;
          }
        }
      }
      return (keys.length === matchCount);
    };
  }

  addFilterValue(docs: Array<any>): Array<any> {
    for (const doc of docs) {
      doc['filterValue'] = doc.notice_title;
      doc['filterValueType'] = this.TYPE_SEARCH_WORD_PREFIX + doc.notice_type;
      let filterValue = '';
      for (const value of doc.project_ids) {
        filterValue += value + ' '
      }
      doc['filterValueProjects'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchFilter['filterValue'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

  applyFilterType(event: any) {
    this.selection.clear();
    let filterValue = '';
    if (event.value) {
      filterValue = this.TYPE_SEARCH_WORD_PREFIX + event.value;
    }
    this.searchFilter['filterValueType'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

  applyFilterProject(event: any) {
    this.selection.clear();
    let filterValue = '';
    if (event.value) {
      filterValue = event.value;
    }
    this.searchFilter['filterValueProjects'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

}
